import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';

const ORG_COLORS = [
  '#22c55e',
  '#3b82f6',
  '#f59e0b',
  '#ec4899',
  '#8b5cf6',
  '#14b8a6',
  '#ef4444',
  '#06b6d4',
];

type ViewMode = 'totaal' | 'organisaties';

interface BucketData {
  key: string;
  label: string;
  users: number;
}

interface OrgBucketData {
  orgName: string;
  buckets: BucketData[];
}

interface UsersOverTimeChartProps {
  className?: string;
  startTime: Date | null;
  endTime: Date;
  timeFrame: string;
  userIds?: string[];
}

/**
 * Shows active users over time, fetched from the activity table.
 * Supports a "totaal" view and an "organisaties" breakdown.
 */
export function UsersOverTimeChart({
  className,
  startTime,
  endTime,
  timeFrame,
  userIds,
}: UsersOverTimeChartProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('totaal');
  const [totalBuckets, setTotalBuckets] = useState<BucketData[]>([]);
  const [orgBuckets, setOrgBuckets] = useState<OrgBucketData[]>([]);
  const [enabledOrgs, setEnabledOrgs] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  // Determine bucket granularity from the dashboard time frame
  const granularity = useMemo(() => {
    switch (timeFrame) {
      case '24uur':
        return 'hour' as const;
      case '7dagen':
      case '30dagen':
        return 'day' as const;
      case '3maanden':
        return 'week' as const;
      case 'altijd':
      default:
        return 'month' as const;
    }
  }, [timeFrame]);

  // Fetch activity data and bucket it
  useEffect(() => {
    async function fetchData() {
      setLoading(true);

      // Build query
      let query = supabase
        .from('activity')
        .select('user_id, updated_at')
        .not('content_id', 'is', null);

      if (startTime) {
        query = query.gte('updated_at', startTime.toISOString());
      }
      query = query.lte('updated_at', endTime.toISOString());
      if (userIds && userIds.length > 0) {
        query = query.in('user_id', userIds);
      }

      const { data: activities } = await query;
      if (!activities || activities.length === 0) {
        setTotalBuckets([]);
        setOrgBuckets([]);
        setLoading(false);
        return;
      }

      // Generate empty bucket keys for the time range
      const bucketKeys = generateBucketKeys(startTime, endTime, granularity);

      // Total: count distinct users per bucket
      const totalMap: Record<string, Set<string>> = {};
      for (const bk of bucketKeys) {
        totalMap[bk.key] = new Set();
      }

      for (const act of activities) {
        const key = toBucketKey(new Date(act.updated_at), granularity);
        if (totalMap[key]) {
          totalMap[key].add(act.user_id);
        }
      }

      setTotalBuckets(
        bucketKeys.map((bk) => ({
          key: bk.key,
          label: bk.label,
          users: totalMap[bk.key]?.size || 0,
        })),
      );

      // Organization breakdown
      if (viewMode === 'organisaties') {
        await fetchOrgBreakdown(activities, bucketKeys);
      }

      setLoading(false);
    }

    fetchData();
  }, [startTime?.toISOString(), endTime.toISOString(), granularity, userIds?.join(',')]);

  // Refetch org breakdown when switching to org view
  useEffect(() => {
    if (viewMode !== 'organisaties' || totalBuckets.length === 0) return;

    async function refetch() {
      let query = supabase
        .from('activity')
        .select('user_id, updated_at')
        .not('content_id', 'is', null);

      if (startTime) {
        query = query.gte('updated_at', startTime.toISOString());
      }
      query = query.lte('updated_at', endTime.toISOString());

      const { data: activities } = await query;
      if (!activities) return;

      const bucketKeys = generateBucketKeys(startTime, endTime, granularity);
      await fetchOrgBreakdown(activities, bucketKeys);
    }

    if (orgBuckets.length === 0) {
      refetch();
    }
  }, [viewMode]);

  async function fetchOrgBreakdown(
    activities: { user_id: string; updated_at: string }[],
    bucketKeys: { key: string; label: string }[],
  ) {
    // Get user → organization mapping via team_user_link → team → organization
    const userIds = [...new Set(activities.map((a) => a.user_id))];

    const { data: links } = await supabase
      .from('team_user_link')
      .select('user_id, team!inner(organization!inner(id, name))')
      .in('user_id', userIds);

    // Build user → org name map
    const userOrgMap: Record<string, string> = {};
    const orgNames = new Set<string>();

    if (links) {
      for (const link of links) {
        const org = (link as any).team?.organization;
        if (org?.name) {
          userOrgMap[link.user_id] = org.name;
          orgNames.add(org.name);
        }
      }
    }

    // Users not in any org
    const hasUnlinked = userIds.some((uid) => !userOrgMap[uid]);
    if (hasUnlinked) {
      orgNames.add('Individueel');
    }

    // Bucket activities per org
    const orgMap: Record<string, Record<string, Set<string>>> = {};
    for (const orgName of orgNames) {
      orgMap[orgName] = {};
      for (const bk of bucketKeys) {
        orgMap[orgName][bk.key] = new Set();
      }
    }

    for (const act of activities) {
      const key = toBucketKey(new Date(act.updated_at), granularity);
      const orgName = userOrgMap[act.user_id] || 'Individueel';
      if (orgMap[orgName]?.[key]) {
        orgMap[orgName][key].add(act.user_id);
      }
    }

    const result: OrgBucketData[] = [...orgNames].map((orgName) => ({
      orgName,
      buckets: bucketKeys.map((bk) => ({
        key: bk.key,
        label: bk.label,
        users: orgMap[orgName][bk.key]?.size || 0,
      })),
    }));

    setOrgBuckets(result);

    // Enable all orgs by default
    const newEnabled: Record<string, boolean> = {};
    for (const orgName of orgNames) {
      newEnabled[orgName] = enabledOrgs[orgName] ?? true;
    }
    setEnabledOrgs(newEnabled);
  }

  // Determine which data series to render
  const series = useMemo(() => {
    if (viewMode === 'organisaties' && orgBuckets.length > 0) {
      return orgBuckets
        .filter((ob) => enabledOrgs[ob.orgName])
        .map((ob, i) => ({
          name: ob.orgName,
          data: ob.buckets,
          color: ORG_COLORS[i % ORG_COLORS.length],
        }));
    }

    return [
      {
        name: 'Totaal',
        data: totalBuckets,
        color: '#22c55e',
      },
    ];
  }, [viewMode, totalBuckets, orgBuckets, enabledOrgs]);

  // Find max value across all visible series
  const maxValue = useMemo(() => {
    let max = 0;
    for (const s of series) {
      for (const b of s.data) {
        if (b.users > max) max = b.users;
      }
    }
    return Math.ceil(max * 1.1) || 1;
  }, [series]);

  const bucketCount = totalBuckets.length || 1;

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-4">
        <div className="relative">
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value as ViewMode)}
            className="h-8 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 appearance-none pr-8"
          >
            <option value="totaal">Totaal</option>
            <option value="organisaties">Organisaties</option>
          </select>
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2.5 4.5L6 8L9.5 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {viewMode === 'organisaties' && orgBuckets.length > 0 && (
          <div className="flex flex-wrap gap-3 text-sm">
            {orgBuckets.map((ob, i) => (
              <div
                key={ob.orgName}
                className="flex items-center gap-1.5 cursor-pointer"
                onClick={() =>
                  setEnabledOrgs((prev) => ({
                    ...prev,
                    [ob.orgName]: !prev[ob.orgName],
                  }))
                }
              >
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{
                    backgroundColor: enabledOrgs[ob.orgName]
                      ? ORG_COLORS[i % ORG_COLORS.length]
                      : '#e5e7eb',
                  }}
                />
                <span
                  className="truncate max-w-[120px]"
                  style={{
                    opacity: enabledOrgs[ob.orgName] ? 1 : 0.4,
                  }}
                >
                  {ob.orgName}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center h-[300px] text-muted-foreground">
          <div className="animate-spin mr-2 h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full" />
          Laden...
        </div>
      )}

      {!loading && totalBuckets.length === 0 && (
        <div className="flex items-center justify-center h-[300px] text-muted-foreground">
          Geen activiteit in deze periode
        </div>
      )}

      {!loading && totalBuckets.length > 0 && (
        <div className="relative h-[300px] mb-6">
          {/* Y-axis label */}
          <div className="absolute -left-1 top-1/2 -translate-y-1/2 -rotate-90 text-xs text-gray-400 whitespace-nowrap">
            Gebruikers
          </div>
          {/* Y-axis values */}
          <div className="absolute left-6 top-0 h-full flex flex-col justify-between text-xs text-gray-500">
            <span>{maxValue}</span>
            <span>{Math.round(maxValue / 2)}</span>
            <span>0</span>
          </div>
          <div className="absolute left-12 right-0 h-full">
            {/* Grid lines */}
            <div className="absolute w-full h-full flex flex-col justify-between">
              <div className="border-b border-gray-100 w-full" />
              <div className="border-b border-gray-100 w-full" />
              <div className="border-b border-gray-200 w-full" />
            </div>

            {/* SVG chart */}
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              {series.map((s) => {
                const points = s.data
                  .map((b, i) => {
                    const x =
                      s.data.length === 1
                        ? 50
                        : (i / (s.data.length - 1)) * 100;
                    const y = 100 - (b.users / maxValue) * 100;
                    return `${x},${y}`;
                  })
                  .join(' ');

                // Area fill (only for single series)
                const areaPoints =
                  series.length === 1 ? `0,100 ${points} 100,100` : null;

                return (
                  <g key={s.name}>
                    {areaPoints && (
                      <polygon
                        points={areaPoints}
                        fill={s.color}
                        fillOpacity="0.1"
                      />
                    )}
                    <polyline
                      points={points}
                      fill="none"
                      stroke={s.color}
                      strokeWidth="2"
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                );
              })}
            </svg>

            {/* Data points (separate SVG for proper circle sizing) */}
            <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
              {series.map((s) =>
                s.data.map((b, i) => {
                  const x =
                    s.data.length === 1
                      ? 50
                      : (i / (s.data.length - 1)) * 100;
                  const y = 100 - (b.users / maxValue) * 100;
                  return (
                    <g key={`${s.name}-${i}`}>
                      <circle
                        cx={`${x}%`}
                        cy={`${y}%`}
                        r="3"
                        fill="white"
                        stroke={s.color}
                        strokeWidth="2"
                      />
                      <title>
                        {s.name}: {b.users} gebruikers ({b.label})
                      </title>
                    </g>
                  );
                }),
              )}
            </svg>

            {/* X-axis labels */}
            <div className="absolute w-full top-full flex justify-between text-xs text-gray-500 mt-2">
              {totalBuckets.map((b, i) => {
                if (
                  bucketCount <= 8 ||
                  i % Math.ceil(bucketCount / 8) === 0 ||
                  i === bucketCount - 1
                ) {
                  return <span key={b.key}>{b.label}</span>;
                }
                return <span key={b.key} />;
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper: generate bucket key from a date
function toBucketKey(
  d: Date,
  granularity: 'hour' | 'day' | 'week' | 'month',
): string {
  switch (granularity) {
    case 'hour':
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}`;
    case 'day':
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    case 'week': {
      // ISO week bucketing: use the Monday of the week
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(d);
      monday.setDate(diff);
      return `${monday.getFullYear()}-W${pad(Math.ceil((monday.getDate() + new Date(monday.getFullYear(), monday.getMonth(), 1).getDay()) / 7))}`;
    }
    case 'month':
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  }
}

// Helper: generate all bucket keys + labels for the time range
function generateBucketKeys(
  startTime: Date | null,
  endTime: Date,
  granularity: 'hour' | 'day' | 'week' | 'month',
): { key: string; label: string }[] {
  const keys: { key: string; label: string }[] = [];
  const seen = new Set<string>();
  const start = startTime || new Date(endTime.getTime() - 365 * 24 * 60 * 60 * 1000);

  const cursor = new Date(start);

  while (cursor <= endTime) {
    const key = toBucketKey(cursor, granularity);
    if (!seen.has(key)) {
      seen.add(key);
      keys.push({ key, label: formatLabel(cursor, granularity) });
    }

    // Advance cursor
    switch (granularity) {
      case 'hour':
        cursor.setHours(cursor.getHours() + 1);
        break;
      case 'day':
        cursor.setDate(cursor.getDate() + 1);
        break;
      case 'week':
        cursor.setDate(cursor.getDate() + 7);
        break;
      case 'month':
        cursor.setMonth(cursor.getMonth() + 1);
        break;
    }
  }

  return keys;
}

function formatLabel(
  d: Date,
  granularity: 'hour' | 'day' | 'week' | 'month',
): string {
  switch (granularity) {
    case 'hour':
      return `${pad(d.getHours())}:00`;
    case 'day':
      return `${d.getDate()}/${d.getMonth() + 1}`;
    case 'week':
      return `${d.getDate()}/${d.getMonth() + 1}`;
    case 'month':
      return d.toLocaleString('nl', { month: 'short', year: '2-digit' });
  }
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}
